import { useCallback, useMemo, useState } from "react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandSeparator,
} from "@/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/shared/components/ui/toggle-group";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import {
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import { MonitorIoTClassUtil } from "@/services/backend/qpm/lib/monitor-iot-class-util";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";

export type MonitorSelectComponentProps = {
  monitorList: MonitorSnapDTO[];
  onMonitorSelect: (monitorId: string) => void;
};

type MonitorFilterKey = MonitorType | "TOX";

const MONITOR_FILTERS: MonitorFilterKey[] = [
  MonitorType.Checkpoint,
  MonitorType.FlyExterminator,
  MonitorType.IoT,
  "TOX",
];

const IOT_CLASS_GROUPS: Array<{
  key: string;
  heading: string;
  classes: MonitorIoTClass[];
}> = [
  {
    key: "monitor",
    heading: MonitorIoTClassUtil.toString(MonitorIoTClass.IoTPestMonitor),
    classes: [
      MonitorIoTClass.IoTPestMonitor,
      MonitorIoTClass.IoTPestMonitorWithSim,
    ],
  },
  {
    key: "gateway",
    heading: MonitorIoTClassUtil.toString(MonitorIoTClass.IoTGateway),
    classes: [MonitorIoTClass.IoTGateway],
  },
  {
    key: "sensor",
    heading: MonitorIoTClassUtil.toString(MonitorIoTClass.IoTSensor),
    classes: [MonitorIoTClass.IoTSensor],
  },
  {
    key: "sim",
    heading: MonitorIoTClassUtil.toString(MonitorIoTClass.IoTSim),
    classes: [MonitorIoTClass.IoTSim],
  },
  {
    key: "portal",
    heading: MonitorIoTClassUtil.toString(MonitorIoTClass.IoTPortal),
    classes: [MonitorIoTClass.IoTPortal],
  },
];

export function MonitorSelectSearchComponent({
  monitorList,
  onMonitorSelect,
}: MonitorSelectComponentProps) {
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<MonitorFilterKey | null>(
    null,
  );

  const groupedIoTMonitors = useMemo(
    () =>
      IOT_CLASS_GROUPS.map((group) => ({
        key: group.key,
        heading: group.heading,
        monitors: monitorList
          .filter(
            (monitor) =>
              monitor.type === MonitorType.IoT &&
              !monitor.capabilities.hasLethalAgent &&
              group.classes.some(
                (cls) => cls === monitor.capabilities.iot?.class,
              ),
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      })).filter((group) => group.monitors.length > 0),
    [monitorList],
  );

  const monitorGroups = useMemo(
    () => [
      {
        key: MonitorType.Checkpoint as MonitorFilterKey,
        heading: MonitorTypeUtil.toLongString(MonitorType.Checkpoint),
        monitors: monitorList
          .filter(
            (monitor) =>
              monitor.type === MonitorType.Checkpoint &&
              !monitor.capabilities.hasLethalAgent,
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      },
      {
        key: MonitorType.FlyExterminator as MonitorFilterKey,
        heading: MonitorTypeUtil.toLongString(MonitorType.FlyExterminator),
        monitors: monitorList
          .filter(
            (monitor) =>
              monitor.type === MonitorType.FlyExterminator &&
              !monitor.capabilities.hasLethalAgent,
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      },
      {
        key: "TOX" as const,
        heading: t("Tox"),
        monitors: monitorList
          .filter((monitor) => monitor.capabilities.hasLethalAgent)
          .sort((a, b) => a.name.localeCompare(b.name)),
      },
    ],
    [monitorList],
  );

  const shouldShowGroup = useCallback(
    (key: MonitorFilterKey) => !activeFilter || activeFilter === key,
    [activeFilter],
  );

  const visibleMonitorGroups = useMemo(
    () =>
      monitorGroups.filter(
        (group) => group.monitors.length > 0 && shouldShowGroup(group.key),
      ),
    [monitorGroups, shouldShowGroup],
  );

  const availableFilters = useMemo(
    () =>
      MONITOR_FILTERS.filter((key) => {
        if (key === MonitorType.IoT) {
          return groupedIoTMonitors.some((group) => group.monitors.length > 0);
        }
        return monitorGroups.some(
          (group) => group.key === key && group.monitors.length > 0,
        );
      }),
    [groupedIoTMonitors, monitorGroups],
  );

  function toggle(filter: MonitorFilterKey) {
    setActiveFilter((prev) => (prev === filter ? null : filter));
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-75 justify-between"
        >
          {t("Monitor auswählen")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-75 p-0">
        <Command className="max-h-72">
          <CommandInput placeholder={t("Monitor suchen...")} className="h-9" />
          {availableFilters.length > 0 ? (
            <ToggleGroup
              type="single"
              size="sm"
              className="my-1 w-full items-start justify-start px-1"
              value={activeFilter || undefined}
              onValueChange={(value) => toggle(value as MonitorFilterKey)}
            >
              {availableFilters.map((key) => (
                <ToggleGroupItem
                  key={`${key}_toggle-item`}
                  value={key}
                  variant="outline"
                  aria-label={`${t("Toggle")} ${key}`}
                  className="border-2 px-2 py-1 text-center text-xs"
                >
                  {key === "TOX" ? t("Tox") : t(MonitorTypeUtil.toString(key))}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          ) : null}
          <ScrollArea className="w-full overflow-auto overscroll-y-contain rounded-md border">
            {visibleMonitorGroups.map((group) => (
              <div key={group.key}>
                <CommandGroup heading={group.heading}>
                  {group.monitors.map((monitor) => (
                    <CommandItem
                      key={monitor.sourceId}
                      value={monitor.name}
                      onSelect={() => {
                        setOpen(false);
                        onMonitorSelect(monitor.sourceId);
                      }}
                    >
                      {monitor.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </div>
            ))}
            {shouldShowGroup(MonitorType.IoT) && (
              <div>
                {groupedIoTMonitors.map(({ key, heading, monitors }) => (
                  <div key={key}>
                    <CommandGroup heading={heading}>
                      {monitors.map((monitor) => (
                        <CommandItem
                          key={monitor.sourceId}
                          value={monitor.name}
                          onSelect={() => {
                            setOpen(false);
                            onMonitorSelect(monitor.sourceId);
                          }}
                        >
                          {monitor.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandSeparator />
                  </div>
                ))}
              </div>
            )}
            <CommandEmpty>{t("Monitor nicht vorhanden.")}</CommandEmpty>
          </ScrollArea>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
